import { ClientResponse } from "@/Common/Api/ClientResponse";
import { Guid } from "@/Common/Models/Guid";
import { CreateFlowsheetTemplateScope } from "../Api/Contracts/CreateFlowsheetTemplate";
import { FlowsheetTemplateClient } from "../Api/FlowsheetTemplatesClient";
import { FlowsheetConstants } from "../FlowsheetConstants";
import { CareElementOperatorType } from "../Models/CareElementOperatorType";
import { CareElementConfig } from "../Models/CareElements/CareElementConfig";
import { CareElementType } from "../Models/CareElements/CareElementType";
import { FlowsheetConfig } from "../Models/CareElements/FlowsheetConfig";
import { LabResultsCareElementConfig } from "../Models/CareElements/LabResultsCareElementConfig";
import { VitalSignsCareElementConfig } from "../Models/CareElements/VitalSignsCareElementConfig";
import { useCommand, UseCommandOptions, UseCommandResult } from "./useCommand";

interface UseCreateFlowsheetTemplateRequest {
    name: string;
    flowsheetTemplateScope: CreateFlowsheetTemplateScope;
    flowsheetTemplateConfiguration: FlowsheetConfig;
    flowsheetId?: Guid;
    patientId?: Guid;
}

interface UseCreateFlowsheetTemplateResult extends UseCommandResult<ClientResponse, any, UseCreateFlowsheetTemplateRequest> {
    createFlowsheetTemplate: (request: UseCreateFlowsheetTemplateRequest) => Promise<void>;
}

interface UseCreateFlowsheetTemplateOptions extends UseCommandOptions<ClientResponse<void>, Error> {
}

function useCreateFlowsheetTemplate(options?: UseCreateFlowsheetTemplateOptions): UseCreateFlowsheetTemplateResult {
    async function command(requestInfo: UseCreateFlowsheetTemplateRequest): Promise<ClientResponse> {
        const careComponentConfigurations = requestInfo.flowsheetTemplateConfiguration.CareComponents.map(careComponent => ({
            Name: careComponent.Name,
            CareElements: ProcessCareElementConfigs(careComponent.CareElementsConfig),
            IsExpanded: careComponent.Expanded
        }));

        const request = {
            FlowsheetId: requestInfo.flowsheetId,
            PatientId: requestInfo.patientId,
            FlowsheetTemplateCreateType: requestInfo.flowsheetTemplateScope,
            Name: requestInfo.name,
            CareComponentConfigurations: careComponentConfigurations
        };

        const response = await FlowsheetTemplateClient.createFlowsheetTemplate(request);

        response.throwIfError();

        return response;
    }

    const mutation = useCommand(command, options);

    return {
        ...mutation,
        createFlowsheetTemplate: mutation.mutate
    };
}

function ProcessCareElementConfigs(careElementConfigs: CareElementConfig[]): CareElementConfig[] {
    return careElementConfigs?.flatMap(careElementConfig => {
        switch (careElementConfig.CareElementType) {
            case CareElementType.VitalSigns: {
                const vitalSignsConfig = careElementConfig as VitalSignsCareElementConfig;
                if (careElementConfig.Name === FlowsheetConstants.VitalSigns.BloodPressureCareElementName) {
                    return processBloodPressureConfig(vitalSignsConfig);
                }
                else {
                    return [processTargetRange(vitalSignsConfig)];
                }
            }
            case CareElementType.LabResults: {
                const labResultsConfig = careElementConfig as LabResultsCareElementConfig;
                return [processTargetRange(labResultsConfig)];
            }
            default:
                return [careElementConfig];
        }
    });
}

/**
 * Processes the blood pressure configuration to split it into two separate care elements.
 */
function processBloodPressureConfig(bloodPressureConfig: VitalSignsCareElementConfig): CareElementConfig[] {
    const { TargetRange } = bloodPressureConfig;

    const isValidTargetRange = TargetRange && !IsNullOrUndefined(TargetRange.LowerBound) && !IsNullOrUndefined(TargetRange.UpperBound);

    return [
        {
            ...bloodPressureConfig,
            Name: FlowsheetConstants.VitalSigns.BloodPressureSystolicCareElementName,
            TargetRange: isValidTargetRange ? { LowerBound: TargetRange.LowerBound } : null
        },
        {
            ...bloodPressureConfig,
            Name: FlowsheetConstants.VitalSigns.BloodPressureDiastolicCareElementName,
            TargetRange: isValidTargetRange ? { LowerBound: TargetRange.UpperBound } : null
        }
    ];
}

/**
 * Handles the target range and operator for lab results and vital signs care elements.
 * Validates the target range and operator, clearing them if necessary to avoid invalid configurations.
 */
function processTargetRange(
    careElementConfig: LabResultsCareElementConfig | VitalSignsCareElementConfig
): CareElementConfig {
    const { Operator, TargetRange } = careElementConfig;

    const isOperatorEmpty = Operator === CareElementOperatorType.None || Operator == null;

    const hasInvalidInBetweenBounds =
        Operator === CareElementOperatorType.Between
        && (!TargetRange || IsNullOrUndefined(TargetRange.LowerBound) || IsNullOrUndefined(TargetRange.UpperBound));

    const hasInvalidTarget =
        (!isOperatorEmpty && Operator !== CareElementOperatorType.Between)
        && (!TargetRange || IsNullOrUndefined(TargetRange.LowerBound));

    const shouldClearTargetRange = isOperatorEmpty || hasInvalidInBetweenBounds || hasInvalidTarget;
    const shouldClearOperator = hasInvalidTarget || hasInvalidInBetweenBounds;

    return {
        ...careElementConfig,
        TargetRange: shouldClearTargetRange ? null : TargetRange,
        Operator: shouldClearOperator ? CareElementOperatorType.None : Operator
    };
}

export {
    useCreateFlowsheetTemplate
};
